/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface DeclineReasonProps {
  id: string;
  name: string;
  abbreviation: string;
  declineCategoryId: string;
  controllable: boolean;
  canBeSkipReason: boolean;
  canBeAbortReason: boolean;
  canBeBulkSkipReason: boolean;
  canBeOneCallSkipReason: boolean;
  isUnitAvailableAfterAbort: boolean;
  secondaryReasonRequired: boolean;
  cancellationReasonId: string | null;
  cancellationSecondaryReasonId: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class DeclineReasonRecord extends Record<DeclineReasonProps>({
  id: null as any,
  name: null as any,
  abbreviation: null as any,
  declineCategoryId: null as any,
  controllable: null as any,
  canBeSkipReason: null as any,
  canBeAbortReason: null as any,
  canBeBulkSkipReason: null as any,
  canBeOneCallSkipReason: null as any,
  isUnitAvailableAfterAbort: null as any,
  secondaryReasonRequired: null as any,
  cancellationReasonId: null as any,
  cancellationSecondaryReasonId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    abbreviation: string;
    declineCategoryId: string;
    controllable: boolean;
    canBeSkipReason: boolean;
    canBeAbortReason: boolean;
    canBeBulkSkipReason: boolean;
    canBeOneCallSkipReason: boolean;
    isUnitAvailableAfterAbort: boolean;
    secondaryReasonRequired: boolean;
    cancellationReasonId: string | null;
    cancellationSecondaryReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): DeclineReasonRecord {
    if (json == null) {
      throw new Error('DeclineReasonRecord.fromJS: parameter cannot be null');
    }
    return new DeclineReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'DeclineReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'DeclineReasonJson.name'),
      abbreviation: getFromOrThrow(json, (j) => j.abbreviation, 'DeclineReasonJson.abbreviation'),
      declineCategoryId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'declineCategoryId', '00000000-0000-0000-0000-000000000000'),
        'DeclineReasonJson.declineCategoryId',
      ),
      controllable: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'controllable', false),
        'DeclineReasonJson.controllable',
      ),
      canBeSkipReason: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'canBeSkipReason', false),
        'DeclineReasonJson.canBeSkipReason',
      ),
      canBeAbortReason: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'canBeAbortReason', false),
        'DeclineReasonJson.canBeAbortReason',
      ),
      canBeBulkSkipReason: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'canBeBulkSkipReason', false),
        'DeclineReasonJson.canBeBulkSkipReason',
      ),
      canBeOneCallSkipReason: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'canBeOneCallSkipReason', false),
        'DeclineReasonJson.canBeOneCallSkipReason',
      ),
      isUnitAvailableAfterAbort: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isUnitAvailableAfterAbort', false),
        'DeclineReasonJson.isUnitAvailableAfterAbort',
      ),
      secondaryReasonRequired: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'secondaryReasonRequired', false),
        'DeclineReasonJson.secondaryReasonRequired',
      ),
      cancellationReasonId: json.cancellationReasonId,
      cancellationSecondaryReasonId: json.cancellationSecondaryReasonId,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    abbreviation: string;
    declineCategoryId: string;
    controllable: boolean;
    canBeSkipReason: boolean;
    canBeAbortReason: boolean;
    canBeBulkSkipReason: boolean;
    canBeOneCallSkipReason: boolean;
    isUnitAvailableAfterAbort: boolean;
    secondaryReasonRequired: boolean;
    cancellationReasonId: string | null;
    cancellationSecondaryReasonId: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [DeclineReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('DeclineReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new DeclineReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'DeclineReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'DeclineReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      abbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.abbreviation, 'DeclineReasonJson.abbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      declineCategoryId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'declineCategoryId', '00000000-0000-0000-0000-000000000000'),
            'DeclineReasonJson.declineCategoryId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      controllable: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'controllable', false),
            'DeclineReasonJson.controllable',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      canBeSkipReason: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'canBeSkipReason', false),
            'DeclineReasonJson.canBeSkipReason',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      canBeAbortReason: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'canBeAbortReason', false),
            'DeclineReasonJson.canBeAbortReason',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      canBeBulkSkipReason: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'canBeBulkSkipReason', false),
            'DeclineReasonJson.canBeBulkSkipReason',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      canBeOneCallSkipReason: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'canBeOneCallSkipReason', false),
            'DeclineReasonJson.canBeOneCallSkipReason',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isUnitAvailableAfterAbort: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isUnitAvailableAfterAbort', false),
            'DeclineReasonJson.isUnitAvailableAfterAbort',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      secondaryReasonRequired: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'secondaryReasonRequired', false),
            'DeclineReasonJson.secondaryReasonRequired',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationReasonId: tryOrDefault(
        () => json.cancellationReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cancellationSecondaryReasonId: tryOrDefault(
        () => json.cancellationSecondaryReasonId,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: DeclineReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('DeclineReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class DeclineReasonViewModelBase implements DeclineReasonProps {
  protected readonly _props: DeclineReasonProps;
  private _json?: DeclineReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get abbreviation(): string {
    return this._props.abbreviation;
  }
  get declineCategoryId(): string {
    return this._props.declineCategoryId;
  }
  get controllable(): boolean {
    return this._props.controllable;
  }
  get canBeSkipReason(): boolean {
    return this._props.canBeSkipReason;
  }
  get canBeAbortReason(): boolean {
    return this._props.canBeAbortReason;
  }
  get canBeBulkSkipReason(): boolean {
    return this._props.canBeBulkSkipReason;
  }
  get canBeOneCallSkipReason(): boolean {
    return this._props.canBeOneCallSkipReason;
  }
  get isUnitAvailableAfterAbort(): boolean {
    return this._props.isUnitAvailableAfterAbort;
  }
  get secondaryReasonRequired(): boolean {
    return this._props.secondaryReasonRequired;
  }
  get cancellationReasonId(): string | null {
    return this._props.cancellationReasonId;
  }
  get cancellationSecondaryReasonId(): string | null {
    return this._props.cancellationSecondaryReasonId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: DeclineReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        abbreviation: this._props.abbreviation,
        declineCategoryId: this._props.declineCategoryId,
        controllable: this._props.controllable,
        canBeSkipReason: this._props.canBeSkipReason,
        canBeAbortReason: this._props.canBeAbortReason,
        canBeBulkSkipReason: this._props.canBeBulkSkipReason,
        canBeOneCallSkipReason: this._props.canBeOneCallSkipReason,
        isUnitAvailableAfterAbort: this._props.isUnitAvailableAfterAbort,
        secondaryReasonRequired: this._props.secondaryReasonRequired,
        cancellationReasonId: this._props.cancellationReasonId,
        cancellationSecondaryReasonId: this._props.cancellationSecondaryReasonId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllDeclineReasonEntities = (state: AppState) =>
  state.entityStores.declineReason.entities;

export const selectDeclineReasonById = (id: string) => (state: AppState) =>
  state.entityStores.declineReason.entities.get(id);

export const selectDeclineReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.declineReason.meta.get(id);

export const selectDeclineReasonAndMetadataById = (id: string) =>
  createSelector(
    selectDeclineReasonById(id),
    selectDeclineReasonMetadataById(id),
    (declineReason, meta) => ({ declineReason, meta }),
  );

export function selectDeclineReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllDeclineReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, DeclineReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectDeclineReasonEntitiesByName(name: string) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByAbbreviation(abbreviation: string) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.abbreviation === abbreviation).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByDeclineCategoryId(declineCategoryId: string) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.declineCategoryId === declineCategoryId).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByControllable(controllable: boolean) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.controllable === controllable).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByCanBeSkipReason(canBeSkipReason: boolean) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.canBeSkipReason === canBeSkipReason).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByCanBeAbortReason(canBeAbortReason: boolean) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.canBeAbortReason === canBeAbortReason).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByCanBeBulkSkipReason(canBeBulkSkipReason: boolean) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.canBeBulkSkipReason === canBeBulkSkipReason).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByCanBeOneCallSkipReason(
  canBeOneCallSkipReason: boolean,
) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.canBeOneCallSkipReason === canBeOneCallSkipReason).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByIsUnitAvailableAfterAbort(
  isUnitAvailableAfterAbort: boolean,
) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.isUnitAvailableAfterAbort === isUnitAvailableAfterAbort).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesBySecondaryReasonRequired(
  secondaryReasonRequired: boolean,
) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.secondaryReasonRequired === secondaryReasonRequired).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByCancellationReasonId(cancellationReasonId: string) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities.filter((e) => e.cancellationReasonId === cancellationReasonId).valueSeq(),
  );
}

export function selectDeclineReasonEntitiesByCancellationSecondaryReasonId(
  cancellationSecondaryReasonId: string,
) {
  return createSelector(selectAllDeclineReasonEntities, (entities) =>
    entities
      .filter((e) => e.cancellationSecondaryReasonId === cancellationSecondaryReasonId)
      .valueSeq(),
  );
}
//#endregion Selectors
